import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft, Pencil, Lock, LockOpen, RefreshCw, Loader, Upload, RotateCcw, ChevronDown,
  ChevronRight, AlertTriangle, CheckCircle, Info, XCircle, History, ListChecks, Send, Scale,
} from 'lucide-react';
import { apiGescomExport } from '../../api/client.js';
import Modal from '../Modal.jsx';
import GescomExportRunner from './GescomExportRunner.jsx';
import {
  card, sectionTitle, thStyle, tdStyle, primaryBtn, secondaryBtn, pill, inputStyle,
  fmtDay, fmtDateTime, TERMINAL, Counter, StatusBadge, LineStatusChip, JobProgress,
} from './gescomUi.jsx';

/**
 * Détail d'une session d'export Gescom : en-tête (nom, période, clôture) et
 * trois onglets — Suivi (état calculé des lignes, actions par collaborateur,
 * relance des erreurs), Export (flux complet), Journal (événements + jobs).
 */

const TABS = [
  { key: 'status', label: 'Suivi', icon: ListChecks },
  { key: 'export', label: 'Export', icon: Send },
  { key: 'journal', label: 'Journal', icon: History },
];

const STATUS_FILTERS = [
  { key: 'all', label: 'Tous' },
  { key: 'pending', label: 'À pousser' },
  { key: 'error', label: 'En erreur' },
  { key: 'blocked', label: 'Bloqués' },
  { key: 'pushed', label: 'Poussés' },
];

export default function GescomSessionDetail({ sessionId, persons, onBack, onChanged }) {
  const [tab, setTab] = useState('status');
  const [data, setData] = useState(null); // { session, events, jobs }
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(() => new Set());
  const [confirm, setConfirm] = useState(null); // { kind: 'person'|'retry'|'close'|'reopen', person? }
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [journalFilter, setJournalFilter] = useState('all');
  const [openEvents, setOpenEvents] = useState(() => new Set());
  const pollRef = useRef(null);

  const loadSession = useCallback(async () => {
    try {
      setData(await apiGescomExport.session(sessionId));
    } catch (e) {
      setError(e.message || 'Session introuvable');
    }
  }, [sessionId]);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      setStatus(await apiGescomExport.sessionStatus(sessionId));
    } catch (e) {
      setError(e.message || 'Calcul du suivi impossible');
    } finally {
      setStatusLoading(false);
    }
  }, [sessionId]);

  useEffect(() => { void loadSession(); void loadStatus(); }, [loadSession, loadStatus]);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const refreshAll = useCallback(() => {
    void loadSession();
    void loadStatus();
    onChanged?.();
  }, [loadSession, loadStatus, onChanged]);

  /** Suit un job lancé depuis le Suivi (polling léger), puis recharge. */
  const followJob = (jobId) => {
    clearInterval(pollRef.current);
    setJob({ id: jobId, status: 'PENDING', totals: {} });
    pollRef.current = setInterval(async () => {
      try {
        const j = await apiGescomExport.job(jobId);
        setJob(j);
        if (TERMINAL.has(j.status) && !j.nextRetryAt) {
          clearInterval(pollRef.current);
          refreshAll();
        }
      } catch { /* nouvel essai au tick suivant */ }
    }, 2000);
  };

  const session = data?.session ?? status?.session ?? null;
  const closed = session?.status === 'CLOSED';

  const runConfirm = async () => {
    const c = confirm;
    setConfirm(null);
    setBusy(true);
    setError(null);
    try {
      if (c.kind === 'person') {
        const res = await apiGescomExport.push({ sessionId, from: session.from, to: session.to, personIds: [c.person.personId] });
        followJob(res.jobId);
        void loadSession();
      } else if (c.kind === 'resync') {
        const res = await apiGescomExport.resyncSession(sessionId);
        followJob(res.jobId);
        void loadSession();
      } else if (c.kind === 'retry') {
        const res = await apiGescomExport.retrySessionErrors(sessionId);
        followJob(res.jobId);
        void loadSession();
      } else if (c.kind === 'close' || c.kind === 'reopen') {
        await apiGescomExport.updateSession(sessionId, { status: c.kind === 'close' ? 'CLOSED' : 'OPEN' });
        refreshAll();
      }
    } catch (e) {
      setError(e.message || 'Action impossible');
    } finally {
      setBusy(false);
    }
  };

  const saveRename = async () => {
    setBusy(true);
    try {
      await apiGescomExport.updateSession(sessionId, { name: nameDraft.trim(), notes: notesDraft.trim() || null });
      setRenaming(false);
      refreshAll();
    } catch (e) {
      setError(e.message || 'Modification impossible');
    } finally {
      setBusy(false);
    }
  };

  const linesByPerson = useMemo(() => {
    const map = new Map();
    for (const l of status?.lines ?? []) {
      if (!map.has(l.personId)) map.set(l.personId, []);
      map.get(l.personId).push(l);
    }
    return map;
  }, [status]);

  const visiblePersons = useMemo(
    () => (status?.persons ?? []).filter((p) => filter === 'all' || p[filter] > 0),
    [status, filter],
  );

  const errorLines = useMemo(() => (status?.lines ?? []).filter((l) => l.status === 'error'), [status]);
  const jobRunning = job && !TERMINAL.has(job.status);

  const events = useMemo(
    () => (data?.events ?? []).filter((e) => journalFilter === 'all' || e.level === 'error' || e.level === 'warning'),
    [data, journalFilter],
  );
  const errorEventCount = (data?.events ?? []).filter((e) => e.level === 'error' || e.level === 'warning').length;

  if (error && !session) {
    return (
      <div style={card}>
        <button type="button" style={secondaryBtn} onClick={onBack}><ArrowLeft size={14} /> Sessions</button>
        <p style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</p>
      </div>
    );
  }
  if (!session) {
    return <div style={{ padding: 24, color: 'var(--text-muted)', fontSize: 13 }}><Loader size={14} className="spin" /> Chargement de la session…</div>;
  }

  const c = status?.counts;

  return (
    <div>
      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 16 }}>
        <button type="button" style={secondaryBtn} onClick={onBack} title="Retour à la liste des sessions">
          <ArrowLeft size={14} /> Sessions
        </button>
        <h2 style={{ margin: 0, fontSize: 19, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 8 }}>
          {session.name}
          <button
            type="button"
            title="Renommer / annoter"
            onClick={() => { setNameDraft(session.name); setNotesDraft(session.notes ?? ''); setRenaming(true); }}
            style={{ border: 'none', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', padding: 2 }}
          >
            <Pencil size={14} />
          </button>
        </h2>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          du {fmtDay(session.from)} au {fmtDay(session.to)}
        </span>
        <span
          style={{
            fontSize: 12, padding: '2px 10px', borderRadius: 999,
            border: `1px solid ${closed ? 'var(--text-muted)' : 'var(--success)'}`,
            color: closed ? 'var(--text-muted)' : 'var(--success)',
          }}
        >
          {closed ? 'Close' : 'Ouverte'}
        </span>
        <div style={{ marginLeft: 'auto' }}>
          {closed ? (
            <button type="button" style={secondaryBtn} onClick={() => setConfirm({ kind: 'reopen' })}>
              <LockOpen size={14} /> Rouvrir
            </button>
          ) : (
            <button type="button" style={secondaryBtn} onClick={() => setConfirm({ kind: 'close' })}>
              <Lock size={14} /> Clore la session
            </button>
          )}
        </div>
      </div>
      {session.notes ? <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '8px 0 0' }}>{session.notes}</p> : null}
      {error ? <p style={{ fontSize: 13, color: 'var(--danger)', margin: '10px 0 0' }}>{error}</p> : null}

      {/* Onglets */}
      <div style={{ display: 'flex', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
        {TABS.map((t) => {
          const Icon = t.icon;
          const badge = t.key === 'status' ? (c?.error ?? 0) : t.key === 'journal' ? errorEventCount : 0;
          return (
            <button key={t.key} type="button" style={{ ...pill(tab === t.key), display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => setTab(t.key)}>
              <Icon size={14} /> {t.label}
              {badge > 0 ? (
                <span style={{ fontSize: 11, background: 'var(--danger)', color: '#fff', borderRadius: 999, padding: '0 6px' }}>{badge}</span>
              ) : null}
            </button>
          );
        })}
      </div>

      {tab === 'status' ? (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
            <h3 style={sectionTitle}><ListChecks size={16} /> Suivi des lignes</h3>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" style={secondaryBtn} onClick={loadStatus} disabled={statusLoading}>
                {statusLoading ? <Loader size={13} className="spin" /> : <RefreshCw size={13} />} Actualiser
              </button>
              {(c?.outOfSync ?? 0) > 0 ? (
                <button
                  type="button"
                  style={{ ...secondaryBtn, color: 'var(--warning)', borderColor: 'var(--warning)', opacity: closed || jobRunning ? 0.5 : 1 }}
                  disabled={closed || jobRunning || busy}
                  title="Remet dans Gescom la valeur synolog des jours envoyés puis modifiés ou supprimés dans Gescom"
                  onClick={() => setConfirm({ kind: 'resync' })}
                >
                  <Scale size={14} /> Resynchroniser ({c.outOfSync})
                </button>
              ) : null}
              <button
                type="button"
                style={{ ...primaryBtn, opacity: (c?.error ?? 0) > 0 && !closed && !jobRunning ? 1 : 0.5 }}
                disabled={!c?.error || closed || jobRunning || busy}
                title={closed ? 'Session close' : 'Relance uniquement les lignes en erreur'}
                onClick={() => setConfirm({ kind: 'retry' })}
              >
                <RotateCcw size={14} /> Relancer les erreurs ({c?.error ?? 0})
              </button>
            </div>
          </div>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '8px 0 0' }}>
            État recalculé à chaque actualisation, sur toute la période : une saisie modifiée après son envoi repasse « À pousser », une affaire liée entre-temps sort des « Bloquées ».
          </p>

          {status && status.gescomCheckAvailable === false ? (
            <p style={{ fontSize: 12.5, color: 'var(--warning)', margin: '8px 0 0' }}>
              <AlertTriangle size={13} style={{ verticalAlign: 'middle' }} /> Imputations Gescom illisibles : comparaison avec Gescom non faite.
            </p>
          ) : null}
          {(c?.conflicts ?? 0) > 0 ? (
            <p style={{ fontSize: 12.5, color: 'var(--warning)', margin: '8px 0 0' }}>
              <Scale size={13} style={{ verticalAlign: 'middle' }} /> {c.conflicts} jour(s) déjà occupé(s) dans Gescom hors synolog (comptés en « Bloquées ») : à remplacer, ou non, ligne par ligne dans l’onglet Export.
            </p>
          ) : null}
          {status?.catalogUnavailable ? (
            <p style={{ fontSize: 12.5, color: 'var(--warning)', margin: '8px 0 0' }}>
              <AlertTriangle size={13} style={{ verticalAlign: 'middle' }} /> Catalogue Gescom illisible : contrôles BCS non faits.
            </p>
          ) : null}

          {statusLoading && !status ? (
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}><Loader size={13} className="spin" /> Calcul de l’état des lignes…</p>
          ) : c ? (
            <>
              <div style={{ marginTop: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Counter label="Poussées" value={c.pushed} color="var(--success)" active={filter === 'pushed'} onClick={() => setFilter(filter === 'pushed' ? 'all' : 'pushed')} />
                <Counter label="À pousser" value={c.pending} color="var(--accent)" active={filter === 'pending'} onClick={() => setFilter(filter === 'pending' ? 'all' : 'pending')} />
                <Counter label="En erreur" value={c.error} color="var(--danger)" active={filter === 'error'} onClick={() => setFilter(filter === 'error' ? 'all' : 'error')} />
                <Counter label="Bloquées" value={c.blocked} color="var(--warning)" active={filter === 'blocked'} onClick={() => setFilter(filter === 'blocked' ? 'all' : 'blocked')} />
                <Counter label="Avec avertissement" value={c.warnings} />
                <Counter label="Écarts Gescom" value={c.outOfSync ?? 0} color={c.outOfSync ? 'var(--warning)' : undefined} />
              </div>

              {job ? (
                <div style={{ marginTop: 16, border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-sm)', padding: 12 }}>
                  <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 8 }}>
                    {jobRunning ? <Loader size={13} className="spin" /> : <CheckCircle size={13} />} Job en cours de suivi
                  </div>
                  <JobProgress job={job} />
                </div>
              ) : null}

              <div style={{ display: 'flex', gap: 6, marginTop: 18, flexWrap: 'wrap' }}>
                {STATUS_FILTERS.map((f) => (
                  <button key={f.key} type="button" style={pill(filter === f.key)} onClick={() => setFilter(f.key)}>{f.label}</button>
                ))}
              </div>

              <div style={{ marginTop: 10, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                      <th style={thStyle}>Collaborateur</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Poussées</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>À pousser</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>En erreur</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Bloquées</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visiblePersons.length === 0 ? (
                      <tr><td colSpan={6} style={{ ...tdStyle, color: 'var(--text-muted)' }}>Aucun collaborateur dans ce filtre.</td></tr>
                    ) : visiblePersons.map((p) => {
                      const open = expanded.has(p.personId);
                      const toPush = p.pending + p.error;
                      const lines = (linesByPerson.get(p.personId) ?? []).filter((l) => filter === 'all' || l.status === filter);
                      return (
                        <Fragment key={p.personId}>
                          <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                            <td style={tdStyle}>
                              <button
                                type="button"
                                onClick={() => setExpanded((cur) => { const n = new Set(cur); if (n.has(p.personId)) n.delete(p.personId); else n.add(p.personId); return n; })}
                                style={{ border: 'none', background: 'transparent', color: 'var(--text)', cursor: 'pointer', padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4, font: 'inherit' }}
                              >
                                {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />} {p.personName}
                              </button>
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right', color: p.pushed ? 'var(--success)' : 'var(--text-muted)' }}>{p.pushed}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', color: p.pending ? 'var(--accent)' : 'var(--text-muted)' }}>{p.pending}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', color: p.error ? 'var(--danger)' : 'var(--text-muted)' }}>{p.error}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', color: p.blocked ? 'var(--warning)' : 'var(--text-muted)' }}>{p.blocked}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                              {toPush > 0 ? (
                                <button
                                  type="button"
                                  disabled={closed || jobRunning || busy}
                                  title={closed ? 'Session close' : `Pousser les ${toPush} ligne(s) à pousser de ${p.personName}`}
                                  onClick={() => setConfirm({ kind: 'person', person: p })}
                                  style={{ ...secondaryBtn, padding: '3px 10px', fontSize: 12, color: 'var(--accent)', borderColor: 'var(--accent)', opacity: closed || jobRunning ? 0.5 : 1 }}
                                >
                                  <Upload size={12} /> Pousser
                                </button>
                              ) : null}
                            </td>
                          </tr>
                          {open ? (
                            <tr>
                              <td colSpan={6} style={{ padding: '4px 8px 12px 28px', background: 'var(--glass)' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                  <tbody>
                                    {lines.map((l) => (
                                      <tr key={l.key} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                                        <td style={{ ...tdStyle, width: 92 }}><LineStatusChip status={l.status} /></td>
                                        <td style={{ ...tdStyle, width: 86 }}>{fmtDay(l.iso)}</td>
                                        <td style={tdStyle}>{l.affaireName ?? '— (congé)'}</td>
                                        <td style={{ ...tdStyle, textAlign: 'right', width: 60 }}>{l.days} j</td>
                                        <td style={{ ...tdStyle, color: l.status === 'error' ? 'var(--danger)' : l.status === 'blocked' ? 'var(--warning)' : 'var(--warning)' }}>
                                          {l.error ?? (l.reasons.length ? l.reasons.join(' · ') : l.warnings.join(' · '))}
                                          {l.pushState === 'partial' && l.status !== 'pushed' ? (
                                            <span style={{ color: 'var(--text-muted)' }}> · saisie modifiée après le dernier envoi</span>
                                          ) : null}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {errorLines.length > 0 ? (
                <div style={{ marginTop: 18 }}>
                  <h4 style={{ margin: '0 0 6px', fontSize: 13.5, color: 'var(--danger)' }}>Lignes en erreur ({errorLines.length})</h4>
                  <div style={{ maxHeight: 220, overflowY: 'auto', background: 'var(--glass)', border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-sm)', padding: 10, fontSize: 12 }}>
                    {errorLines.map((l) => (
                      <div key={l.key} style={{ padding: '2px 0', color: 'var(--text)' }}>
                        <strong>{l.personName}</strong> · {l.affaireName} · {fmtDay(l.iso)} — <span style={{ color: 'var(--danger)' }}>{l.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      {tab === 'export' ? (
        <GescomExportRunner session={session} persons={persons} onJobFinished={refreshAll} />
      ) : null}

      {tab === 'journal' ? (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
            <h3 style={sectionTitle}><History size={16} /> Journal de la session</h3>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" style={pill(journalFilter === 'all')} onClick={() => setJournalFilter('all')}>Tout</button>
              <button type="button" style={pill(journalFilter === 'errors')} onClick={() => setJournalFilter('errors')}>Erreurs et alertes</button>
              <button type="button" style={secondaryBtn} onClick={loadSession}><RefreshCw size={13} /></button>
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            {events.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Aucun événement.</p>
            ) : events.map((e) => {
              const errs = Array.isArray(e.data?.errors) ? e.data.errors : [];
              const open = openEvents.has(e.id);
              return (
                <div key={e.id} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--glass-border)' }}>
                  <LevelIcon level={e.level} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: 'var(--text)' }}>{e.message}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                      {fmtDateTime(e.at)}{e.user ? ` · ${e.user}` : ' · système'}
                    </div>
                    {errs.length > 0 ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setOpenEvents((cur) => { const n = new Set(cur); if (n.has(e.id)) n.delete(e.id); else n.add(e.id); return n; })}
                          style={{ marginTop: 4, border: 'none', background: 'transparent', color: 'var(--danger)', cursor: 'pointer', padding: 0, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        >
                          {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />} {errs.length} erreur{errs.length > 1 ? 's' : ''}
                        </button>
                        {open ? (
                          <div style={{ marginTop: 4, fontSize: 12, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: 'var(--danger)' }}>
                            {errs.map((x, i) => (
                              <div key={i}>{x.personName} · {x.affaireName} · {fmtDay(x.iso)} — {x.error}</div>
                            ))}
                          </div>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>

          <h4 style={{ margin: '22px 0 8px', fontSize: 13.5, color: 'var(--text)' }}>Jobs de la session ({data?.jobs?.length ?? 0})</h4>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                  <th style={thStyle}>Lancé le</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Périmètre</th>
                  <th style={thStyle}>Statut</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>OK / échec / ignoré</th>
                  <th style={thStyle}>Par</th>
                </tr>
              </thead>
              <tbody>
                {(data?.jobs ?? []).map((j) => (
                  <tr key={j.id} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                    <td style={tdStyle}>{fmtDateTime(j.createdAt)}</td>
                    <td style={tdStyle}>{j.type === 'PUSH' ? 'Export' : 'Suppression'}</td>
                    <td style={tdStyle}>
                      {j.scope.lineCount ? `${j.scope.lineCount} ligne(s) ciblée(s)`
                        : j.scope.personCount ? `${j.scope.personCount} collaborateur(s)` : 'Tous'}
                      {j.scope.excludedCount ? ` · ${j.scope.excludedCount} exclu(s)` : ''}
                    </td>
                    <td style={tdStyle}><StatusBadge status={j.status} /></td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {j.totals ? `${j.totals.success ?? 0} / ${j.totals.failed ?? 0} / ${j.totals.skipped ?? 0}` : '—'}
                    </td>
                    <td style={tdStyle}>{j.createdBy ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* Confirmations */}
      <Modal
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        title={
          confirm?.kind === 'person' ? `Pousser ${confirm.person.personName}`
            : confirm?.kind === 'resync' ? 'Resynchroniser avec Gescom'
            : confirm?.kind === 'retry' ? 'Relancer les lignes en erreur'
              : confirm?.kind === 'close' ? 'Clore la session' : 'Rouvrir la session'
        }
        footer={
          <>
            <button type="button" style={secondaryBtn} onClick={() => setConfirm(null)}>Annuler</button>
            <button type="button" style={primaryBtn} onClick={runConfirm} disabled={busy}>Confirmer</button>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text)' }}>
          {confirm?.kind === 'person'
            ? `Pousse vers Gescom les ${confirm.person.pending + confirm.person.error} ligne(s) non encore poussée(s) de ${confirm.person.personName} sur la période de la session. Les lignes bloquées restent ignorées.`
            : confirm?.kind === 'resync'
              ? `Remet dans Gescom la valeur synolog de ${c?.outOfSync ?? 0} jour(s) envoyé(s) puis modifié(s) ou supprimé(s) dans Gescom : l’imputation Gescom de ces jours sera supprimée puis remplacée. Les jours occupés hors synolog ne sont pas concernés.`
            : confirm?.kind === 'retry'
              ? `Relance exactement les ${c?.error ?? 0} ligne(s) en erreur, sans repousser les autres lignes.`
              : confirm?.kind === 'close'
                ? 'Une session close reste consultable (suivi, journal) mais n’accepte plus d’export ni de suppression. Elle peut être rouverte.'
                : 'La session accepte de nouveau les exports et les suppressions.'}
        </p>
      </Modal>

      <Modal
        open={renaming}
        onClose={() => setRenaming(false)}
        title="Renommer la session"
        footer={
          <>
            <button type="button" style={secondaryBtn} onClick={() => setRenaming(false)}>Annuler</button>
            <button type="button" style={primaryBtn} onClick={saveRename} disabled={busy || !nameDraft.trim()}>Enregistrer</button>
          </>
        }
      >
        <label style={{ display: 'block', fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>Nom</label>
        <input value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} maxLength={120} style={inputStyle} />
        <label style={{ display: 'block', fontSize: 13, color: 'var(--text-muted)', margin: '12px 0 6px' }}>Notes</label>
        <textarea value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)} maxLength={2000} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 0 }}>La période d’une session est figée : créez une autre session pour une autre période.</p>
      </Modal>
    </div>
  );
}

function LevelIcon({ level }) {
  const props = { size: 16, style: { flexShrink: 0, marginTop: 2 } };
  if (level === 'error') return <XCircle {...props} style={{ ...props.style, color: 'var(--danger)' }} />;
  if (level === 'warning') return <AlertTriangle {...props} style={{ ...props.style, color: 'var(--warning)' }} />;
  if (level === 'success') return <CheckCircle {...props} style={{ ...props.style, color: 'var(--success)' }} />;
  return <Info {...props} style={{ ...props.style, color: 'var(--text-muted)' }} />;
}
